import { dist, pt } from '../model/geom';
import type { CadEntity, DimensionEntity } from '../model/entity';
import { formatLength } from '../model/units';
import type { Command, CmdContext, CommandRunner } from './types';

/**
 * 치수 명령 (작업지시서 P2-13)
 *   DIMLINEAR · DIMALIGNED · DIMRADIUS
 *
 * 마트 도면에서 치수는 "통로 폭 2400" 같은 검증 근거로 쓰인다.
 * 따라서 실측값을 자동 표기하는 것이 기본이고, 수동 덮어쓰기는 예외로 둔다.
 */

function dimBase(ctx: CmdContext) {
  return {
    id: ctx.newId(),
    // 치수는 항상 주석 레이어로 간다 — 도면 정리 시 한 번에 껐다 켤 수 있어야 한다
    layer: 'A-DIM',
    textHeight: 200_000, // 200mm
  };
}

/* ------------------------------------------------------------ DIMLINEAR */

export const DIMLINEAR: Command = {
  name: 'DIMLINEAR',
  aliases: ['DLI', 'DIM'],
  label: '치수 (수평/수직)',
  hint: '두 점 사이의 가로 또는 세로 길이를 적습니다',
  *run(ctx: CmdContext): CommandRunner {
    const unit = ctx.scene.doc.displayUnit;

    const a = yield { type: 'point', message: '첫 번째 치수보조선 지정' };
    if (a.t !== 'point') return;

    const b = yield {
      type: 'point',
      message: '두 번째 치수보조선 지정',
      base: a.p,
      preview: (cursor) => [
        { id: 'preview', layer: 'A-DIM', type: 'line', a: a.p, b: cursor } as CadEntity,
      ],
    };
    if (b.t !== 'point') return;

    const off = yield {
      type: 'point',
      message: '치수선 위치 지정',
      base: a.p,
      preview: (cursor) => [
        {
          ...dimBase(ctx),
          id: 'preview',
          type: 'dimension',
          kind: 'linear',
          a: a.p,
          b: b.p,
          offsetPoint: cursor,
        } as CadEntity,
      ],
    };
    if (off.t !== 'point') return;

    // 가로/세로 중 더 긴 쪽을 잰다 (AutoCAD 의 DIMLINEAR 기본 동작)
    const dx = Math.abs(b.p.x - a.p.x);
    const dy = Math.abs(b.p.y - a.p.y);
    const measured = dx >= dy ? dx : dy;
    ctx.log(`치수: ${formatLength(measured, unit)} ${unit}`);

    const dim: DimensionEntity = {
      ...dimBase(ctx),
      type: 'dimension',
      kind: 'linear',
      a: a.p,
      b: b.p,
      offsetPoint: off.p,
      overrideText: null,
    };

    return [{ kind: 'add', command: 'DIMLINEAR', entities: [dim] }];
  },
};

/* ----------------------------------------------------------- DIMALIGNED */

export const DIMALIGNED: Command = {
  name: 'DIMALIGNED',
  aliases: ['DAL'],
  label: '치수 (정렬)',
  hint: '두 점을 잇는 실제 거리를 적습니다 (비스듬한 벽 등)',
  *run(ctx: CmdContext): CommandRunner {
    const unit = ctx.scene.doc.displayUnit;

    const a = yield { type: 'point', message: '첫 번째 점 지정' };
    if (a.t !== 'point') return;

    const b = yield {
      type: 'point',
      message: '두 번째 점 지정',
      base: a.p,
      preview: (cursor) => [
        { id: 'preview', layer: 'A-DIM', type: 'line', a: a.p, b: cursor } as CadEntity,
      ],
    };
    if (b.t !== 'point') return;

    const off = yield {
      type: 'point',
      message: '치수선 위치 지정',
      base: a.p,
      preview: (cursor) => [
        {
          ...dimBase(ctx),
          id: 'preview',
          type: 'dimension',
          kind: 'aligned',
          a: a.p,
          b: b.p,
          offsetPoint: cursor,
        } as CadEntity,
      ],
    };
    if (off.t !== 'point') return;

    ctx.log(`치수: ${formatLength(Math.round(dist(a.p, b.p)), unit)} ${unit}`);

    const dim: DimensionEntity = {
      ...dimBase(ctx),
      type: 'dimension',
      kind: 'aligned',
      a: a.p,
      b: b.p,
      offsetPoint: off.p,
      overrideText: null,
    };

    return [{ kind: 'add', command: 'DIMALIGNED', entities: [dim] }];
  },
};

/* ------------------------------------------------------------ DIMRADIUS */

export const DIMRADIUS: Command = {
  name: 'DIMRADIUS',
  aliases: ['DRA'],
  label: '치수 (반지름)',
  hint: '원이나 호의 반지름을 적습니다',
  *run(ctx: CmdContext): CommandRunner {
    const unit = ctx.scene.doc.displayUnit;

    const sel = yield { type: 'selection', message: '원 또는 호 선택', useExisting: true };
    if (sel.t !== 'selection') return;

    const target = sel.ids
      .map((id) => ctx.scene.get(id))
      .find((e) => e && (e.type === 'circle' || e.type === 'arc'));

    if (!target || (target.type !== 'circle' && target.type !== 'arc')) {
      ctx.log('원이나 호를 선택해야 합니다.');
      return;
    }

    const off = yield {
      type: 'point',
      message: '치수선 위치 지정',
      base: target.center,
    };
    if (off.t !== 'point') return;

    ctx.log(`반지름: ${formatLength(target.radius, unit)} ${unit}`);

    const dim: DimensionEntity = {
      ...dimBase(ctx),
      type: 'dimension',
      kind: 'radius',
      a: target.center,
      b: pt(target.center.x + target.radius, target.center.y),
      offsetPoint: off.p,
      overrideText: null,
    };

    return [{ kind: 'add', command: 'DIMRADIUS', entities: [dim] }];
  },
};

export const DIMENSION_COMMANDS: Command[] = [DIMLINEAR, DIMALIGNED, DIMRADIUS];

/* -------------------------------------------------------------------------- *
 * 표기 문자열 — 렌더러가 쓴다
 * -------------------------------------------------------------------------- */

/** 치수에 표기할 문자열. overrideText 가 있으면 그걸, 없으면 실측값. */
export function dimensionLabel(
  e: DimensionEntity,
  unit: 'mm' | 'cm' | 'm'
): string {
  if (e.overrideText) return e.overrideText;

  switch (e.kind) {
    case 'linear': {
      const dx = Math.abs(e.b.x - e.a.x);
      const dy = Math.abs(e.b.y - e.a.y);
      return formatLength(dx >= dy ? dx : dy, unit);
    }
    case 'aligned':
      return formatLength(Math.round(dist(e.a, e.b)), unit);
    case 'radius':
      return `R ${formatLength(Math.round(dist(e.a, e.b)), unit)}`;
    case 'angular':
      return '';
  }
}
